import type { Author, Course, Learn, LearnItemRelationResponseCollection } from '@edwin-group/cj-graphql';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

type LearnToStart = { idModule: number | string; idLesson: number | string };

export function getCourseCategoriesByLearn(courses: Course[] | null) {
  if (!courses || courses?.length === 0) {
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const categories = courses?.reduce<{ [key: string]: any }>((acc, curr) => {
    const category = curr?.attributes?.course_category?.data?.attributes;

    if (!category) {
      return acc;
    }

    if (acc[`${category.name}`]) {
      return acc;
    }

    return {
      ...acc,
      [`${category.name}`]: {
        name: category.name,
        description: category.description,
      },
    };
  }, {}) as { [key: string]: { name?: string; description?: string } };

  return categories;
}

export function getStartLesson(course: Course, learn?: Learn | null) {
  const lessons = course?.modules?.reduce((acc, curr) => {
    const moduleLessons = curr?.lessons?.map((lesson) => ({
      idLesson: lesson?.lessonStableId || '',
      idModule: curr?.moduleStableId || '',
    })) as LearnToStart[];

    return acc.concat(moduleLessons);
  }, [] as LearnToStart[]);

  if ((!learn?.id && (lessons || []).length > 0) || learn?.attributes?.completed) {
    return { idModule: lessons![0]?.idModule, idLesson: lessons![0]?.idLesson } as LearnToStart;
  }

  const learnItems =
    (learn?.attributes?.learn_items as unknown as LearnItemRelationResponseCollection)?.data?.map((learnItem) => {
      return { idModule: learnItem.attributes?.idModule, idLesson: learnItem.attributes?.idLesson } as LearnToStart;
    }) || [];

  const lessonsToStudy = differenceWith(lessons, learnItems, isEqual);

  return lessonsToStudy.length >= 1 ? lessonsToStudy[0] : null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getAuthors(course?: any): Array<Author> | null {
  if (!course) {
    return null;
  }

  let authors = [] as Array<Author>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (course?.attributes?.modules ?? course?.modules)?.forEach((m: { lessons: any[] }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    m?.lessons?.forEach((lesson: any) => {
      const authorsData = lesson?.authors?.data ?? lesson?.authors ?? [];
      authors = authors.concat(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        authorsData?.map((author: { data: any }) => {
          const authorData = author?.data ?? author;

          return authorData;
        }),
      );
    });
  });

  return uniqBy(authors, 'attributes.name') as Array<Author>;
}

export const getCurrentLearn = (
  course?: Course | null,
  idModule?: number | string | null,
  idLesson?: number | string | null,
) => {
  let nextLesson = null;
  let nextModule = null;
  let previousLessonidModule = idModule;
  let previousIdLesson = null;
  let idNextLessonModule = idModule;

  if (!course || !idModule || !idLesson) {
    return { currentModule: null, currentLesson: null, nextLesson: null };
  }

  const currentModule = course?.modules?.find((m) => m?.moduleStableId === idModule);

  if (!currentModule || currentModule?.lessons?.length === 0) {
    return { currentModule: null, currentLesson: null, nextLesson: null };
  }

  const currentModuleIndex = course?.modules?.findIndex((m) => m?.moduleStableId === idModule);
  const currentLesson = currentModule?.lessons?.find((l) => l?.lessonStableId === idLesson);

  if (!currentLesson) {
    return { currentModule, currentLesson: null, nextLesson: null };
  }

  const currentLessonIndex = currentModule?.lessons?.findIndex((l) => l?.lessonStableId === idLesson);

  if (currentModuleIndex && currentModuleIndex > 0) {
    const previousModule = (course?.modules || [])[currentModuleIndex - 1];
    const previousModuleLessons = previousModule?.lessons || [];

    if (currentLessonIndex === 0 && previousModuleLessons.length > 0) {
      previousLessonidModule = previousModule?.moduleStableId;
      previousIdLesson = previousModuleLessons[previousModuleLessons.length - 1]?.lessonStableId;
    } else if (currentLessonIndex === 0 && previousModuleLessons.length === 0) {
      previousLessonidModule = null;
      previousIdLesson = null;
    }
  }

  if (typeof currentLessonIndex === 'number' && currentLessonIndex > 0) {
    previousLessonidModule = currentModule.moduleStableId;
    previousIdLesson = (currentModule.lessons || [])[currentLessonIndex - 1]?.lessonStableId;
  }

  if (typeof currentLessonIndex === 'number' && (currentModule.lessons || []).length - 1 > currentLessonIndex) {
    nextLesson = (currentModule.lessons || [])[currentLessonIndex + 1];
  }

  if (
    typeof currentModuleIndex === 'number' &&
    (currentModule.lessons || []).length - 1 === currentLessonIndex &&
    (course?.modules || []).length - 1 > currentModuleIndex
  ) {
    nextModule = (course?.modules || [])[currentModuleIndex + 1];
    nextLesson = (nextModule?.lessons || [])[0];
    idNextLessonModule = nextModule?.moduleStableId;
  }

  return { currentModule, currentLesson, nextLesson, idNextLessonModule, previousLessonidModule, previousIdLesson };
};
